import { ImpressionArea } from 'react-simplikit';

import { SocialPostCard } from './social-post-card';
import { SocialPostCardSkeleton } from './social-post-card-skeleton';

import type { PostListItem } from '@/entities/post/post-types';
import { Stage } from '@/shared/components/stage/stage';
import { cn } from '@/shared/utils/cn';

interface Props<TPost extends PostListItem = PostListItem> {
  posts: TPost[];
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  className?: string;
  enableInfiniteScroll?: boolean;
  skeletonCount?: number;
  selectedPostIds?: string[];
  getPostSelectionId?: (post: TPost) => string;
  onTogglePostSelect?: (post: TPost) => void;
  savedBoardCountsByPostId?: Record<string, number>;
  onRemovePost?: (postId: string) => void;
  removeTooltip?: string;
  postFooter?: {
    date: keyof Pick<PostListItem, 'date' | 'updated_at' | 'scraped_at'>;
    label: string;
  };
}

export function SocialPostsGrid<TPost extends PostListItem = PostListItem>({
  posts,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
  className,
  enableInfiniteScroll = true,
  skeletonCount = 12,
  selectedPostIds,
  getPostSelectionId = (post) => post._id,
  onTogglePostSelect,
  savedBoardCountsByPostId,
  onRemovePost,
  removeTooltip,
  postFooter,
}: Props<TPost>) {
  return (
    <Stage>
      <Stage.Scene status={isLoading}>
        <GridContainer className={className}>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <SocialPostCardSkeleton key={i} />
          ))}
        </GridContainer>
      </Stage.Scene>
      <Stage.Scene status={posts.length === 0}>
        <NoPosts />
      </Stage.Scene>
      <Stage.Scene default>
        <>
          <GridContainer className={cn(className, 'relative')}>
            {posts.map((post) => (
              <SocialPostCard
                key={post._id}
                post={post}
                isSelected={selectedPostIds?.includes(getPostSelectionId(post))}
                onToggleSelect={onTogglePostSelect}
                savedBoardCount={savedBoardCountsByPostId?.[post._id] ?? 0}
                onRemove={onRemovePost}
                removeTooltip={removeTooltip}
                footerLabel={postFooter?.label}
                footerUpdatedAt={postFooter ? post[postFooter?.date] || post.scraped_at : undefined}
              />
            ))}

            {enableInfiniteScroll && posts.length > 0 && hasNextPage && onLoadMore && (
              <div className="absolute bottom-0 left-0 h-96 w-full">
                <ImpressionArea className="size-full" onImpressionStart={onLoadMore} />
              </div>
            )}
          </GridContainer>

          {enableInfiniteScroll && isFetchingNextPage && <LoadingMoreIndicator />}
        </>
      </Stage.Scene>
    </Stage>
  );
}

function NoPosts() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-zinc-100">
          <svg
            className="h-10 w-10 text-zinc-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-semibold text-zinc-900">No posts found</h3>
        <p className="text-sm leading-relaxed text-zinc-500">
          We couldn't find any posts matching your criteria. Try adjusting your search parameters or
          date range.
        </p>
      </div>
    </div>
  );
}

function GridContainer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(`relative grid gap-4 p-6`, className)}
      style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(264px, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function LoadingMoreIndicator() {
  return (
    <div className="flex justify-center py-6">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          <div className="h-2 w-2 animate-pulse rounded-full bg-zinc-400 [animation-delay:0ms]" />
          <div className="h-2 w-2 animate-pulse rounded-full bg-zinc-400 [animation-delay:150ms]" />
          <div className="h-2 w-2 animate-pulse rounded-full bg-zinc-400 [animation-delay:300ms]" />
        </div>
        <span className="text-sm text-zinc-500">Loading more posts</span>
      </div>
    </div>
  );
}
